/* oxlint-disable typescript/no-unsafe-argument -- slider onValueChange receives any[] from primitive; cast is intentional, see slider.stories.tsx */
import { IconButton } from '@w/ui/components/icon-button'
import {
  DownloadIcon,
  PauseIcon,
  PlayIcon,
  Volume2Icon,
  VolumeXIcon,
} from '@w/ui/components/icon'
import { Progress } from '@w/ui/components/progress'
import { Slider } from '@w/ui/components/slider'
import { cn } from '@w/ui/lib/utils'
import { useAudio, type UseAudioOptions } from './use-audio'
import { useAudioOptions } from './audio-options-ctx'

type AudioPlayerStatus = 'loading' | 'playing' | 'paused' | 'blocked' | 'error'

export interface AudioPlayerProps {
  src: string
  /** Icon-button-only player. */
  minimal?: boolean
  /** Use icon-button-only player when loaded audio is shorter than this. */
  minimalBelowMs?: number
  /** Stretch to fill the container (UPage audio blocks use this). */
  fullWidth?: boolean
  /** Show a download button. Opt-in - unni had it always on. */
  download?: boolean
  autoplay?: boolean
  onEnd?: () => void
  className?: string
  /**
   * Test/story-only: injects a custom audio controller (e.g. a
   * MockAudioBackend, or one that never resolves to demo the loading
   * state). Never set this in real app code.
   */
  _useAudioOptions?: UseAudioOptions
}

export function AudioPlayer({
  src,
  minimal,
  minimalBelowMs,
  fullWidth,
  download,
  autoplay,
  onEnd,
  className,
  _useAudioOptions,
}: AudioPlayerProps) {
  const audioOptions = useAudioOptions()
  const {
    ready,
    duration,
    progress,
    playing,
    status,
    setProgress,
    volume,
    play,
    pause,
    setVolume,
  } = useAudio({ url: src, autoplay, onEnd }, _useAudioOptions ?? audioOptions)

  const togglePlay = () => {
    if (playing) pause()
    else void play()
  }

  if (!ready) {
    const audioStatus: AudioPlayerStatus =
      status === 'error' ? 'error' : 'loading'
    // A span (phrasing content): ClozeAnswerAudio places the player inside a paragraph line.
    return (
      <span
        className={cn('flex h-10 w-full items-center', className)}
        data-testid="audio-player-loading"
        data-audio-status={audioStatus}
      >
        <Progress value={null} />
      </span>
    )
  }

  if (minimal || (minimalBelowMs !== undefined && duration < minimalBelowMs)) {
    const audioStatus: AudioPlayerStatus =
      status === 'error'
        ? 'error'
        : status === 'blocked'
          ? 'blocked'
          : playing
            ? 'playing'
            : 'paused'
    return (
      <span
        className={className}
        data-testid="audio-player"
        data-audio-status={audioStatus}
      >
        <IconButton onClick={togglePlay}>
          {playing ? <PauseIcon /> : <PlayIcon />}
          {playing ? 'Pause' : 'Play'}
        </IconButton>
      </span>
    )
  }

  const audioStatus: AudioPlayerStatus =
    status === 'error'
      ? 'error'
      : status === 'blocked'
        ? 'blocked'
        : playing
          ? 'playing'
          : 'paused'
  return (
    <div
      data-testid="audio-player"
      data-audio-status={audioStatus}
      className={cn(
        'border-outline flex items-center gap-1 rounded-full border px-2 py-1',
        fullWidth ? 'w-full' : 'w-fit',
        className,
      )}
    >
      <IconButton onClick={togglePlay}>
        {playing ? <PauseIcon /> : <PlayIcon />}
        {playing ? 'Pause' : 'Play'}
      </IconButton>
      <span className="text-label-small text-on-surface-secondary mr-2 tabular-nums">
        {formatTime(progress)}
      </span>
      <Slider
        data-testid="audio-player-seek"
        value={progress}
        min={0}
        max={duration || 1}
        onValueChange={(v) => {
          setProgress(Array.isArray(v) ? v[0] : v)
        }}
        className="min-w-[min(200px,30vw)] grow [&_[data-slot=slider-thumb]]:invisible"
      />
      <span className="text-label-small text-on-surface-secondary ml-2 tabular-nums">
        {formatTime(duration)}
      </span>
      <IconButton
        onClick={() => {
          setVolume(volume < 1 ? 1 : 0)
        }}
      >
        {volume < 1 ? <VolumeXIcon /> : <Volume2Icon />}
        {volume < 1 ? 'Unmute' : 'Mute'}
      </IconButton>
      {download && (
        <IconButton
          onClick={() => {
            void downloadBlob(src)
          }}
        >
          <DownloadIcon />
          Download
        </IconButton>
      )}
    </div>
  )
}

/** Formats milliseconds as `m:ss` with zero-padded seconds. */
function formatTime(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

async function downloadBlob(src: string, filename = 'download') {
  const blob = await fetch(src).then((r) => r.blob())
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = filename
  document.body.append(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
